import $ from 'jquery';

/**
 * Uploads/removes return photos in the "attach photos" panel of the return details popup
 */
class AttachPhotos {
  /**
   * @param {jQuery} $element
   * @param {{ onResize?: Function, onRemove?: Function, onChange?: Function }} [options]
   */
  constructor($element, { onResize, onRemove, onChange } = {}) {
    this.$el = $element;
    this.onResize = onResize || (() => {});
    this.onRemove = onRemove || (() => {});
    this.onChange = onChange || (() => {});
    this.$list = this.$el.find('.js-attach-photos-list');
    this.$add = this.$el.find('.attach-photos__add');
    this.$input = this.$el.find('.js-attach-photos-input');
    this.$counter = this.$el.find('.js-attach-photos-counter');
    this.$message = this.$el.find('.js-attach-photos-message');

    this.required = Number.parseInt(this.$counter.data('required'), 10) || 0;
    this.maximum = Number.parseInt(this.$counter.data('maximum'), 10) || 0;
    this.uploadUrl = this.$el.data('upload-url');
    this.deleteUrl = this.$el.data('delete-url');
    this.previewUrl = this.$el.data('preview-url');
    this.previewTemplate = $('#attach-photos__preview-template').html();

    this.itemId = null;
    this.index = 0;
    this.photoIds = [];
    this.pendingUploads = 0;

    this.handleSelect();
    this.handleRemove();
  }

  /**
   * Reset the uploader for a product, optionally pre-filling photos already uploaded for it
   * @param {number|string} itemId
   * @param {number} index unit index of the batch (matches item_photos[id][index])
   * @param {Array<number|string>} [existingIds]
   */
  reset(itemId, index, existingIds = []) {
    this.itemId = itemId;
    this.index = index;
    this.photoIds = [];
    this.pendingUploads = 0;
    this.$list.find('.attach-photos__item').remove();
    this.updateCounter();

    existingIds.forEach((photoId) => this.addPreview(photoId));
  }

  /**
   * Whether required photos have been uploaded
   * @returns {boolean}
   */
  isSatisfied() {
    return this.photoIds.length >= this.required;
  }

  /**
   * Whether the maximum number of photos has been reached (or exceeded)
   * @returns {boolean}
   */
  isMaximumReached() {
    return this.maximum > 0 && this.photoIds.length >= this.maximum;
  }

  /**
   * Whether more photos have been uploaded than the allowed maximum.
   * The upload input allows selecting several files at once, so this can
   * happen even though the "add" control is hidden once the maximum is
   * reached - a single multi-select can already push the count past it in
   * one go, before the upload responses come back.
   * @returns {boolean}
   */
  isOverMaximum() {
    return this.maximum > 0 && this.photoIds.length > this.maximum;
  }

  /**
   * Whether an upload is still in flight - the final photo count isn't settled yet,
   * so submitting now could leave a just-selected photo out of the batch
   * @returns {boolean}
   */
  isUploading() {
    return this.pendingUploads > 0;
  }

  /**
   * @returns {Array<number|string>}
   */
  getPhotoIds() {
    return this.photoIds.slice();
  }

  showRequiredMessage() {
    this.$message.text(this.$message.data('default')).removeClass('hidden');
    this.onResize();
  }

  showUploadError(msg) {
    this.$message.text(msg || this.$message.data('upload-error')).removeClass('hidden');
    this.onResize();
  }

  showMaximumError() {
    this.$message.text(this.$message.data('max-error')).removeClass('hidden');
    this.onResize();
  }

  showUploadingMessage() {
    this.$message.text(this.$message.data('uploading')).removeClass('hidden');
    this.onResize();
  }

  hideMessage() {
    this.$message.addClass('hidden');
  }

  handleSelect() {
    this.$input.on('change', () => {
      const files = Array.from(this.$input.prop('files') || []);

      // Allow selecting the same file(s) again later
      this.$input.val('');
      files.forEach((file) => this.upload(file));
    });
  }

  enableUpload() {
    this.$input.prop('disabled', false);
    this.$add.removeClass('hidden');
  }

  disableUpload() {
    this.$input.prop('disabled', true);
    this.$add.addClass('hidden');
  }

  /**
   * @param {File} file
   */
  upload(file) {
    const formData = new FormData();
    formData.append('item_id', this.itemId);
    formData.append('index', this.index);
    formData.append('photo', file);

    this.pendingUploads += 1;

    $.ajax({
      url: this.uploadUrl,
      method: 'POST',
      dataType: 'json',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      data: formData,
      processData: false,
      contentType: false,
      noPreloader: true,
    }).done((response) => {
      this.pendingUploads -= 1;

      if (response && !response.error && response.data) {
        this.addPreview(response.data.id);
      } else {
        this.showUploadError(response && response.msg);
      }
    }).fail(() => {
      this.pendingUploads -= 1;
      this.showUploadError();
    });
  }

  /**
   * @param {number|string} photoId
   */
  addPreview(photoId) {
    const $item = $(this.previewTemplate).attr('data-photo-id', photoId).insertBefore(this.$add);

    $item.find('.attach-photos__preview').attr('src', `${this.previewUrl}?id=${photoId}`);

    this.photoIds.push(photoId);
    this.updateCounter();
    this.onResize();
    this.onChange();
  }

  handleRemove() {
    this.$list.on('click', '.attach-photos__remove', (event) => {
      event.preventDefault();

      const $item = $(event.currentTarget).closest('.attach-photos__item');
      const photoId = $item.data('photo-id');

      $.ajax({
        url: this.deleteUrl,
        method: 'POST',
        dataType: 'json',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        data: { id: photoId },
        noPreloader: true,
      }).always(() => {
        this.photoIds = this.photoIds.filter((id) => id !== photoId);
        $item.remove();
        this.updateCounter();
        this.onResize();
        this.onChange();
        // Drop any stale reference to this photo even if the popup gets closed without saving
        this.onRemove(photoId);
      });
    });
  }

  updateCounter() {
    this.$counter.text(`${this.photoIds.length}/${this.required}`);

    // Leave the "please wait" message (if shown) alone while uploads are still in
    // flight, so it doesn't get replaced/hidden the moment one of several
    // concurrent uploads finishes - it only resolves once uploading is done
    if (!this.isUploading()) {
      if (this.isOverMaximum()) {
        this.showMaximumError();
      } else {
        this.hideMessage();
      }
    }

    if (this.isMaximumReached()) {
      this.disableUpload();
    } else {
      this.enableUpload();
    }
  }
}

export default AttachPhotos;
